import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCognitoAuthContext } from '@caplend/cognito-auth';
import { ChevronDownIcon, SettingsIcon, SidebarLogoutIcon } from '../../assets/icons';
import Cognito from '../../auth/cognito';
import { oauthLogout } from '../../auth/configureAmplify';
import {
  getInitials,
  getIsCompanyUser,
  getProfileDisplayName,
  getProfileSubtitle,
} from './profileHelpers';

const MENU_ITEM_CLASS =
  'flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-[var(--n800)] transition-colors hover:bg-[var(--n50)] font-[family-name:var(--font-ui)]';

function DashboardUserControls() {
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const { user, gettingUser } = useSelector((state) => state.user);
  const { setHasAccessToken } = useCognitoAuthContext();
  const [menuOpen, setMenuOpen] = useState(false);

  const isCompany = getIsCompanyUser(user);
  const profileName = gettingUser ? '…' : getProfileDisplayName(user, isCompany);
  const initials = gettingUser ? '…' : getInitials(user, isCompany);
  const profileSubtitle = gettingUser ? '…' : getProfileSubtitle(user, isCompany);

  const closeMenu = useCallback(() => setMenuOpen(false), []);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        closeMenu();
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen, closeMenu]);

  const handleAccountSettings = () => {
    closeMenu();
    navigate('/account-settings');
  };

  const handleLogout = () => {
    closeMenu();
    if (process.env.REACT_APP_COGNITO_DOMAIN) {
      oauthLogout();
      return;
    }
    setHasAccessToken(false);
    Cognito.logOut();
  };

  return (
    <div ref={menuRef} className='relative'>
      <button
        type='button'
        onClick={() => setMenuOpen((open) => !open)}
        aria-label='Account menu'
        aria-expanded={menuOpen}
        aria-haspopup='menu'
        className='inline-flex max-w-[220px] cursor-pointer items-center gap-2 rounded-full border border-[var(--color-border)] bg-[var(--white)] py-[5px] pl-[5px] pr-3 font-[family-name:var(--font-ui)] transition-[background,border-color] duration-150 hover:bg-[var(--n50)]'
      >
        <span className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--n800)] text-xs font-semibold text-white'>
          {initials}
        </span>
        <span className='flex min-w-0 flex-col items-start leading-[1.15]'>
          <span className='max-w-[140px] truncate text-[12.5px] font-medium text-[var(--n900)]'>{profileName}</span>
          <span className='text-[10.5px] text-[var(--text-tertiary)]'>{profileSubtitle}</span>
        </span>
        <ChevronDownIcon
          className={`h-3.5 w-3.5 shrink-0 text-[var(--text-tertiary)] transition-transform duration-150 ${menuOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {menuOpen && (
        <div
          role='menu'
          aria-label='Account options'
          className='absolute right-0 top-[calc(100%+8px)] z-50 min-w-[200px] rounded-[12px] border border-[var(--color-border)] bg-[var(--white)] p-1.5 shadow-[var(--shadow-lg)]'
        >
          <button type='button' role='menuitem' onClick={handleAccountSettings} className={MENU_ITEM_CLASS}>
            <SettingsIcon className='h-4 w-4 shrink-0 text-[var(--text-tertiary)]' />
            Account settings
          </button>
          <button type='button' role='menuitem' onClick={handleLogout} className={MENU_ITEM_CLASS}>
            <SidebarLogoutIcon className='h-4 w-4 shrink-0 text-[var(--text-tertiary)]' strokeWidth={1.6} />
            Log out
          </button>
        </div>
      )}
    </div>
  );
}

export default DashboardUserControls;
